import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { StarPicker } from '../components/StarRating';
import { Star, Send, CheckCircle, User, MessageSquare } from 'lucide-react';

export default function SubmitForm() {
  const { businessId } = useParams();
  const [businessName, setBusinessName] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!businessId) return;
    getDoc(doc(db, 'businesses', businessId)).then(snap => {
      setBusinessName(snap.data()?.businessName || '');
    });
  }, [businessId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (message.trim().length < 10) {
      setError('Please write at least 10 characters.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await addDoc(
        collection(db, 'businesses', businessId || 'default', 'testimonials'),
        {
          name: name.trim(),
          message: message.trim(),
          rating,
          status: 'pending',
          businessId: businessId || 'default',
          createdAt: serverTimestamp(),
        }
      );
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-100 border border-gray-100 p-10">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h2>
            <p className="text-gray-500">
              Your testimonial has been submitted for review. We really appreciate your feedback!
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setName('');
                setMessage('');
                setRating(0);
              }}
              className="mt-6 text-sm text-violet-600 hover:text-violet-700 font-medium"
            >
              Submit another review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-violet-600 rounded-2xl mb-4 shadow-lg shadow-violet-200">
            <Star className="w-7 h-7 text-white fill-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {businessName ? `Review ${businessName}` : 'Share Your Experience'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Your review helps others make better decisions</p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-100 border border-gray-100 p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  Your name
                </span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                maxLength={60}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-gray-900 placeholder-gray-400 transition"
                placeholder="Jane Smith"
              />
            </div>

            {/* Star rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall rating
              </label>
              <StarPicker value={rating} onChange={setRating} />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                  Your review
                </span>
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                required
                rows={4}
                maxLength={600}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-gray-900 placeholder-gray-400 transition resize-none"
                placeholder="Tell us about your experience..."
              />
              <p className="text-right text-xs text-gray-400 mt-1">{message.length}/600</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white font-medium rounded-xl transition shadow-sm shadow-violet-200 text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Testimonial
                </>
              )}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-gray-400">
            Your testimonial will be reviewed before being published.
          </p>
        </div>
      </div>
    </div>
  );
}
